import LogoutOutlined from "@mui/icons-material/LogoutOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { AppBar, Avatar, Box, Button, IconButton, Toolbar, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { SECONDARY_COLOR } from "../../_const/color.const";
import { accountService } from "../../_services/accounts.service";
import GlobalSearchAutoComplete from "../autocomplete/standardMultiline.autocomplete";
import { searchService } from "../../_services/search.service";
import { DataTypeEnum } from "../../_common/enum/dataType.enum";

const logoImg = require("../../_assets/images/aserve-logo.png");

const drawerWidth = 240;
const TOOLBAR_HEIGHT_DESKTOP = 64;
const TOOLBAR_HEIGHT_MOBILE = 56;
/** Must match compact `GlobalSearchAutoComplete` outlined input height on mobile */
const MOBILE_TOOLBAR_CONTROL_HEIGHT = 36;
const LOGO_INSET_DESKTOP = 6;
const LOGO_INSET_MOBILE = 4;
const toolbarShadow = "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)";

function userInitials(email: string, name?: string): string {
    if (name?.trim()) {
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
        }
        return (parts[0]?.slice(0, 2) ?? "?").toUpperCase();
    }
    const local = email?.split("@")[0] ?? "";
    return (
        local
            .split(/[._-]/)
            .map((p) => p[0]?.toUpperCase() ?? "")
            .slice(0, 2)
            .join("") || "?"
    );
}

function userRoleLabel(): string {
    if (accountService.isAdmin) return "Administrator";
    if (accountService.isUser) return "User";
    if (accountService.isAgent) return "Agent";
    if (accountService.isClient) return "Client";
    return "User";
}

const verticalRuleSx = {
    width: "1px",
    height: 20,
    mx: 0.5,
    flexShrink: 0,
    bgcolor: "rgba(255,255,255,0.22)",
} as const;

export const AppToolbar = (params: { toggleSidebar: () => void }) => {
    const [ticketId, setTicketId] = useState("");
    const theme = useTheme();
    const brandPrimary = theme.palette.primary.main;

    const user = accountService.currentUserValue;
    const email = (user?.email as string | undefined) ?? "";
    const name = user?.name as string | undefined;
    const initials = userInitials(email, name);

    const searchProps = {
        dataType: DataTypeEnum.SearchCaseTicket,
        value: ticketId,
        name: "chemicalPGId" as const,
        itemsLoader: searchService.searchCaseTicket,
        onChange: () => {},
        toolbarCompact: true,
    };

    const desktopToolbarHeight = { xs: TOOLBAR_HEIGHT_MOBILE, sm: TOOLBAR_HEIGHT_DESKTOP };

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                width: "100%",
                left: 0,
                right: 0,
                backgroundColor: { xs: brandPrimary, md: "transparent" },
                boxShadow: toolbarShadow,
                zIndex: theme.zIndex.appBar,
            }}
        >
            <Toolbar
                disableGutters
                sx={{
                    display: { xs: "none", md: "flex" },
                    flexDirection: "row",
                    alignItems: "stretch",
                    minHeight: desktopToolbarHeight,
                    height: desktopToolbarHeight,
                    maxWidth: "100%",
                    p: 0,
                    boxSizing: "border-box",
                }}
            >
                <Box
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        height: TOOLBAR_HEIGHT_DESKTOP,
                        minHeight: TOOLBAR_HEIGHT_DESKTOP,
                        bgcolor: "#fff",
                        display: "flex",
                        alignItems: "stretch",
                        gap: 0.5,
                        pl: 0.75,
                        pr: 1,
                        borderRight: "1px solid rgba(0,0,0,0.08)",
                        boxSizing: "border-box",
                    }}
                >
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={() => params.toggleSidebar()}
                        edge="start"
                        sx={{
                            alignSelf: "center",
                            color: brandPrimary,
                            flexShrink: 0,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                        }}
                    >
                        <MenuIcon sx={{ fontSize: 28 }} />
                    </IconButton>

                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            py: `${LOGO_INSET_DESKTOP}px`,
                            pl: 0.25,
                            boxSizing: "border-box",
                        }}
                    >
                        <Box
                            component="img"
                            src={logoImg}
                            alt="Aserve"
                            sx={{
                                display: "block",
                                height: "auto",
                                width: "auto",
                                maxHeight: TOOLBAR_HEIGHT_DESKTOP - LOGO_INSET_DESKTOP * 2,
                                maxWidth: "min(100%, 168px)",
                                objectFit: "contain",
                                objectPosition: "left center",
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        bgcolor: brandPrimary,
                        display: "flex",
                        alignItems: "center",
                        px: { md: 2, lg: 2.5 },
                        gap: { md: 1.25, lg: 1.5 },
                        boxSizing: "border-box",
                    }}
                >
                    <Box sx={{ ...verticalRuleSx, bgcolor: "rgba(255,255,255,0.25)" }} />

                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            maxWidth: { md: 340, lg: 400, xl: 440 },
                            display: "flex",
                            alignItems: "center",
                            "& .MuiAutocomplete-root": {
                                width: "100% !important",
                                maxWidth: "100% !important",
                            },
                        }}
                    >
                        <GlobalSearchAutoComplete {...searchProps} />
                    </Box>

                    <Box sx={{ flexGrow: 1, minWidth: 8 }} />

                    {email || name ? (
                        <>
                            <Box sx={verticalRuleSx} />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flexShrink: 0 }}>
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        bgcolor: "rgba(255,255,255,0.2)",
                                        color: "#fff",
                                        border: `1px solid ${SECONDARY_COLOR}99`,
                                        flexShrink: 0,
                                    }}
                                >
                                    {initials}
                                </Avatar>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 0, maxWidth: 200 }}>
                                    <Typography noWrap sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#fff", maxWidth: "100%" }}>
                                        {name || email || "User"}
                                    </Typography>
                                    <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.65)" }}>{userRoleLabel()}</Typography>
                                </Box>
                            </Box>
                        </>
                    ) : null}

                    <Box sx={verticalRuleSx} />

                    <Button
                        color="inherit"
                        onClick={() => accountService.logout()}
                        title="Log out"
                        startIcon={<LogoutOutlined sx={{ fontSize: 18 }} />}
                        sx={{
                            textTransform: "none",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: "rgba(255,255,255,0.8)",
                            minWidth: "auto",
                            px: 1.25,
                            py: 0.5,
                            flexShrink: 0,
                            "&:hover": { bgcolor: "rgba(255,255,255,0.12)", color: "#fff" },
                            "& .MuiButton-startIcon": { mr: { xs: 0, sm: 0.75 } },
                        }}
                    >
                        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                            Log out
                        </Box>
                    </Button>
                </Box>
            </Toolbar>

            <Toolbar
                disableGutters
                sx={{
                    display: { xs: "flex", md: "none" },
                    alignItems: "center",
                    minHeight: TOOLBAR_HEIGHT_MOBILE,
                    height: TOOLBAR_HEIGHT_MOBILE,
                    px: 0.75,
                    gap: 0.75,
                    bgcolor: brandPrimary,
                    boxSizing: "border-box",
                }}
            >
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={() => params.toggleSidebar()}
                    edge="start"
                    sx={{ color: "#fff", flexShrink: 0 }}
                >
                    <MenuIcon />
                </IconButton>

                <Box
                    sx={{
                        flexShrink: 0,
                        height: MOBILE_TOOLBAR_CONTROL_HEIGHT,
                        minHeight: MOBILE_TOOLBAR_CONTROL_HEIGHT,
                        maxHeight: MOBILE_TOOLBAR_CONTROL_HEIGHT,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(255,255,255,0.96)",
                        borderRadius: 1,
                        px: 0.5,
                        py: 0,
                        boxSizing: "border-box",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    }}
                >
                    <Box
                        component="img"
                        src={logoImg}
                        alt="Aserve"
                        sx={{
                            display: "block",
                            height: "auto",
                            width: "auto",
                            maxHeight: MOBILE_TOOLBAR_CONTROL_HEIGHT - 4,
                            maxWidth: 100,
                            objectFit: "contain",
                            objectPosition: "center",
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        py: 0.25,
                        "& .MuiAutocomplete-root": {
                            width: "100% !important",
                            maxWidth: "100% !important",
                            display: "flex",
                            alignItems: "center",
                        },
                    }}
                >
                    <GlobalSearchAutoComplete {...searchProps} />
                </Box>

                <Button
                    color="inherit"
                    onClick={() => accountService.logout()}
                    title="Log out"
                    startIcon={<LogoutOutlined sx={{ fontSize: 18 }} />}
                    sx={{
                        textTransform: "none",
                        color: "rgba(255,255,255,0.85)",
                        minWidth: "auto",
                        px: 0.75,
                        flexShrink: 0,
                        alignSelf: "center",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                    }}
                />
            </Toolbar>
        </AppBar>
    );
};
